import React, { useEffect, useRef, useState } from "react";
import { ErrorMessage, Formik } from "formik";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import * as Yup from "yup";
import { connect } from "react-redux";
import axiosInstance from "../../../axios-instance";
import Loader from "../../../components/Loader/Loader";
import QuickSetupLayout from "../../../components/OnboardingSetup/QuickSetupLayout";
import {
  OnboardingActions,
  OnboardingFieldError,
} from "../../../components/OnboardingSetup/OnboardingSetupLayout";
import { getLocalePhoneCountry } from "../../../components/OnboardingSetup/onboardingSteps";
import {
  getStepOneData,
  saveStepOneData,
} from "../../../actions/admin/quick-setup-actions";
import isEmpty from "../../../validation/is-empty";
import { getAdminBusinessId } from "../../../utils/authHelper";
import { IMAGE_BASE_URL, notification } from "../../../utils/utility";

const Schema = Yup.object().shape({
  company_name: Yup.string().trim().required("Enter your organisation name."),
  phone: Yup.string().trim().required("Enter a phone number."),
  country_id: Yup.string().required("Select a country."),
  company_street: Yup.string().trim().required("Enter a street address."),
  company_city: Yup.string().trim().required("Enter a city."),
  company_state: Yup.string()
    .trim()
    .required("Enter a state, region, or province."),
  company_zip_code: Yup.string().trim().required("Enter a postal or ZIP code."),
});

const initialValues = {
  company_name: "",
  phone: "",
  country_id: "",
  company_street: "",
  company_city: "",
  company_state: "",
  company_zip_code: "",
};

const LogoPlaceholder = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="9" cy="11" r="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M4 17l5-4 3 2 4-5 4 7" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);

const QuickSetupStepOne = (props) => {
  const navigate = useNavigate();
  const previewUrlRef = useRef("");
  const [formValues, setFormValues] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [recordId, setRecordId] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [selectedFile, setSelectedFile] = useState(undefined);
  const phoneCountry = getLocalePhoneCountry();

  useEffect(() => {
    setPageLoading(true);

    axiosInstance
      .get("/countries")
      .then((response) => {
        setCountries(response.data.data.countries || []);
      })
      .catch(() => {
        notification.error("Could not load the country list. Refresh and try again.");
      });

    props.getStepOneData({
      onSuccess: function (response) {
        const { data } = response;
        if (!isEmpty(data)) {
          setRecordId(data.id);
          setFormValues({
            company_name: data.company_name || "",
            phone: data.phone || "",
            company_street: data.company_street || "",
            company_city: data.company_city || "",
            company_state: data.company_state || "",
            company_zip_code: data.company_zip_code || "",
            country_id: data.country_id ? String(data.country_id) : "",
          });
          if (data.company_logo) {
            setLogoPreview(`${IMAGE_BASE_URL}${data.company_logo}`);
          }
        }
        setPageLoading(false);
      },
      onError: function () {
        setPageLoading(false);
      },
    });

    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  const onSelectLogo = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }
    if (file.size > 800 * 1024) {
      notification.error("Please choose an image under 800 KB.");
      event.target.value = "";
      return;
    }
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    const url = URL.createObjectURL(file);
    previewUrlRef.current = url;
    setSelectedFile(file);
    setLogoPreview(url);
  };

  return (
    <>
      <Loader isShowLoader={pageLoading} />
      <Formik
        validationSchema={Schema}
        initialValues={formValues || initialValues}
        enableReinitialize
        onSubmit={(data) => {
          setPageLoading(true);
          const formData = {
            ...data,
            business_id: getAdminBusinessId(),
          };
          if (selectedFile !== undefined) {
            formData.company_logo = selectedFile;
          }
          if (recordId) {
            formData.id = recordId;
          }

          props.saveStepOneData({
            data: formData,
            onSuccess: (response) => {
              if (response.success) {
                notification.success("Record saved successfully");
              }
              setPageLoading(false);
              navigate("/admin/quick-setup-step-two");
            },
            onError: () => {
              notification.error(
                "Could not save organisation details. Please try again."
              );
              setPageLoading(false);
            },
          });
        }}
      >
        {(formik) => {
          const { values, handleChange, handleSubmit, handleBlur, setFieldValue } =
            formik;
          return (
            <QuickSetupLayout
              currentStep={1}
              title="Your organization"
              description="Shown on your public booking page."
            >
              <form
                className="onboarding-setup__form"
                onSubmit={handleSubmit}
                noValidate
              >
                <div className="onboarding-setup__panels">
                  <section className="onboarding-setup__panel">
                    <h2 className="onboarding-setup__panel-title">Organization</h2>
                    <div className="onboarding-setup__field">
                      <label className="onboarding-setup__label" htmlFor="company_name">
                        Organization name <span className="onboarding-setup__req">*</span>
                      </label>
                      <input
                        type="text"
                        className="onboarding-setup__input"
                        name="company_name"
                        id="company_name"
                        placeholder="Acme Studio or your name"
                        autoComplete="organization"
                        value={values.company_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        aria-required="true"
                      />
                      <ErrorMessage name="company_name" component={OnboardingFieldError} />
                    </div>
                    <div className="onboarding-setup__grid">
                      <div className="onboarding-setup__field">
                        <label className="onboarding-setup__label" htmlFor="phone">
                          Phone <span className="onboarding-setup__req">*</span>
                        </label>
                        <PhoneInput
                          country={phoneCountry}
                          enableSearch
                          specialLabel=""
                          value={values.phone}
                          onChange={(value) => setFieldValue("phone", value)}
                          onBlur={() => formik.setFieldTouched("phone", true)}
                          inputProps={{
                            name: "phone",
                            id: "phone",
                            autoComplete: "tel",
                            "aria-required": "true",
                          }}
                        />
                        <ErrorMessage name="phone" component={OnboardingFieldError} />
                      </div>
                      <div className="onboarding-setup__field">
                        <label className="onboarding-setup__label" htmlFor="country_id">
                          Country <span className="onboarding-setup__req">*</span>
                        </label>
                        <select
                          name="country_id"
                          id="country_id"
                          className="onboarding-setup__select"
                          value={values.country_id}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          autoComplete="country"
                          aria-required="true"
                        >
                          <option value="">Select country</option>
                          {countries.map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          ))}
                        </select>
                        <ErrorMessage name="country_id" component={OnboardingFieldError} />
                      </div>
                    </div>
                    <div className="onboarding-setup__field">
                      <span className="onboarding-setup__label">Logo</span>
                      <div className="onboarding-setup__logo-row">
                        <div className="onboarding-setup__logo-preview" aria-hidden="true">
                          {logoPreview ? <img src={logoPreview} alt="" /> : <LogoPlaceholder />}
                        </div>
                        <label className="onboarding-setup__file-btn">
                          <input
                            name="company_logo"
                            type="file"
                            accept="image/png,image/jpeg,image/gif,image/webp"
                            onChange={onSelectLogo}
                          />
                          {logoPreview ? "Replace" : "Upload"}
                        </label>
                      </div>
                    </div>
                  </section>

                  <section className="onboarding-setup__panel">
                    <h2 className="onboarding-setup__panel-title">Address</h2>
                    <div className="onboarding-setup__field">
                      <label className="onboarding-setup__label" htmlFor="company_street">
                        Street address <span className="onboarding-setup__req">*</span>
                      </label>
                      <input
                        type="text"
                        className="onboarding-setup__input"
                        name="company_street"
                        id="company_street"
                        placeholder="Street, building, or P.O. box"
                        autoComplete="street-address"
                        value={values.company_street}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        aria-required="true"
                      />
                      <ErrorMessage name="company_street" component={OnboardingFieldError} />
                    </div>
                    <div className="onboarding-setup__field">
                      <label className="onboarding-setup__label" htmlFor="company_city">
                        City <span className="onboarding-setup__req">*</span>
                      </label>
                      <input
                        type="text"
                        className="onboarding-setup__input"
                        name="company_city"
                        id="company_city"
                        placeholder="City or town"
                        autoComplete="address-level2"
                        value={values.company_city}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        aria-required="true"
                      />
                      <ErrorMessage name="company_city" component={OnboardingFieldError} />
                    </div>
                    <div className="onboarding-setup__grid">
                      <div className="onboarding-setup__field">
                        <label className="onboarding-setup__label" htmlFor="company_state">
                          Region / state <span className="onboarding-setup__req">*</span>
                        </label>
                        <input
                          type="text"
                          className="onboarding-setup__input"
                          name="company_state"
                          id="company_state"
                          placeholder="Region"
                          autoComplete="address-level1"
                          value={values.company_state}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          aria-required="true"
                        />
                        <ErrorMessage name="company_state" component={OnboardingFieldError} />
                      </div>
                      <div className="onboarding-setup__field">
                        <label className="onboarding-setup__label" htmlFor="company_zip_code">
                          Postal / ZIP <span className="onboarding-setup__req">*</span>
                        </label>
                        <input
                          type="text"
                          className="onboarding-setup__input"
                          name="company_zip_code"
                          id="company_zip_code"
                          placeholder="Code"
                          autoComplete="postal-code"
                          value={values.company_zip_code}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          aria-required="true"
                        />
                        <ErrorMessage name="company_zip_code" component={OnboardingFieldError} />
                      </div>
                    </div>
                  </section>
                </div>

                <OnboardingActions showSkip={false} submitLabel="Continue" />
              </form>
            </QuickSetupLayout>
          );
        }}
      </Formik>
    </>
  );
};

export default connect(null, {
  getStepOneData,
  saveStepOneData,
})(QuickSetupStepOne);
